import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useLayoutEffect, useRef, useState, } from "react";
const EDGE_BAND = 56;
const MAX_SHIFT = 72;
const DISPERSION = 0.14;
const LIQUID_MAP_BLUR = 5;
export const NAV_ARCH_TOP_LEAD = 10;
const NAV_ARCH_EDGE_WIDTH = 155.562;
export const INITIAL_NAV_ARCH_SIZE = { w: 1232, h: 94 };
function navArchPath(w, h) {
    const edge = Math.min(NAV_ARCH_EDGE_WIDTH, w / 2);
    const controlOne = edge * (77.79 / NAV_ARCH_EDGE_WIDTH);
    const controlTwo = edge * (84.8602 / NAV_ARCH_EDGE_WIDTH);
    return [
        `M0 0H${w}V${NAV_ARCH_TOP_LEAD}`,
        `C${w - controlTwo} ${NAV_ARCH_TOP_LEAD + 0.631439} ${w - controlOne} ${h} ${w - edge} ${h}`,
        `H${edge}`,
        `C${controlOne} ${h} ${controlTwo} ${NAV_ARCH_TOP_LEAD + 0.612868} 0 ${NAV_ARCH_TOP_LEAD}`,
        "V0Z",
    ].join("");
}
function navArchRimPath(w, h) {
    const edge = Math.min(NAV_ARCH_EDGE_WIDTH, w / 2);
    const controlOne = edge * (77.79 / NAV_ARCH_EDGE_WIDTH);
    const controlTwo = edge * (84.8602 / NAV_ARCH_EDGE_WIDTH);
    return [
        `M0 ${NAV_ARCH_TOP_LEAD}`,
        `C${controlTwo} ${NAV_ARCH_TOP_LEAD + 0.612868} ${controlOne} ${h} ${edge} ${h}`,
        `H${w - edge}`,
        `C${w - controlOne} ${h} ${w - controlTwo} ${NAV_ARCH_TOP_LEAD + 0.631439} ${w} ${NAV_ARCH_TOP_LEAD}`,
    ].join("");
}
export function navArchMask(w, h) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"><path d="${navArchPath(w, h)}" fill="white"/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
// Reuse the same decoded end caps throughout the width animation. Replacing a
// data-URL mask on every ResizeObserver callback can briefly hide the surface
// in Firefox while the next SVG image becomes ready.
function navArchEndMask(side) {
    const { w, h } = INITIAL_NAV_ARCH_SIZE;
    const x = side === "left" ? 0 : w - NAV_ARCH_EDGE_WIDTH;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${NAV_ARCH_EDGE_WIDTH}" height="${h}" viewBox="${x} 0 ${NAV_ARCH_EDGE_WIDTH} ${h}" preserveAspectRatio="none"><path d="${navArchPath(w, h)}" fill="white"/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
const NAV_ARCH_MASK = `${navArchEndMask("left")}, linear-gradient(white, white), ${navArchEndMask("right")}`;
// Half a pixel of overlap at each join prevents fractional-width seams.
const NAV_ARCH_MASK_SIZE = `min(${NAV_ARCH_EDGE_WIDTH}px, 50%) 100%, max(0px, calc(100% - ${NAV_ARCH_EDGE_WIDTH * 2 - 1}px)) 100%, min(${NAV_ARCH_EDGE_WIDTH}px, 50%) 100%`;
const NAV_ARCH_RIM_SLICES = [
    [0, NAV_ARCH_EDGE_WIDTH],
    [NAV_ARCH_EDGE_WIDTH, INITIAL_NAV_ARCH_SIZE.w - NAV_ARCH_EDGE_WIDTH * 2],
    [INITIAL_NAV_ARCH_SIZE.w - NAV_ARCH_EDGE_WIDTH, NAV_ARCH_EDGE_WIDTH],
];
function smoothEdge(distance, edgeBand) {
    if (distance <= -edgeBand || distance > 0)
        return 0;
    const t = 1 + distance / edgeBand;
    return t * t * (3 - 2 * t);
}
function cubicPoint(t, p0, p1, p2, p3) {
    const inverseT = 1 - t;
    return (inverseT ** 3 * p0 +
        3 * inverseT ** 2 * t * p1 +
        3 * inverseT * t ** 2 * p2 +
        t ** 3 * p3);
}
function cubicDerivative(t, p0, p1, p2, p3) {
    const inverseT = 1 - t;
    return (3 * inverseT ** 2 * (p1 - p0) +
        6 * inverseT * t * (p2 - p1) +
        3 * t ** 2 * (p3 - p2));
}
/** Returns the lower arch edge and its outward-facing normal at an x position. */
function navArchEdgeAt(x, w, h) {
    const edge = Math.min(NAV_ARCH_EDGE_WIDTH, w / 2);
    if (x >= edge && x <= w - edge)
        return { y: h, normalX: 0, normalY: 1 };
    const isRight = x > w / 2;
    const localX = isRight ? w - x : x;
    const controlOne = edge * (77.79 / NAV_ARCH_EDGE_WIDTH);
    const controlTwo = edge * (84.8602 / NAV_ARCH_EDGE_WIDTH);
    const top = NAV_ARCH_TOP_LEAD;
    let start = 0;
    let end = 1;
    for (let iteration = 0; iteration < 10; iteration++) {
        const t = (start + end) / 2;
        if (cubicPoint(t, 0, controlTwo, controlOne, edge) < localX)
            start = t;
        else
            end = t;
    }
    const t = (start + end) / 2;
    const y = cubicPoint(t, top, top + 0.612868, h, h);
    const dx = cubicDerivative(t, 0, controlTwo, controlOne, edge);
    const dy = cubicDerivative(t, top, top + 0.612868, h, h);
    const length = Math.hypot(dx, dy) || 1;
    const normalX = -dy / length;
    const normalY = dx / length;
    return { y, normalX: isRight ? -normalX : normalX, normalY };
}
function makeArchDisplacementMap(w, h, edgeBand) {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext("2d");
    if (!context)
        return null;
    const image = context.createImageData(w, h);
    const data = image.data;
    // The same lower edge applies to every pixel in a column. Precompute it so
    // the map can be regenerated on every nav-width animation frame.
    const edges = Array.from({ length: w }, (_, x) => navArchEdgeAt(x + 0.5, w, h));
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const pointY = y + 0.5;
            const archEdge = edges[x];
            const topInset = pointY;
            const bottomInset = archEdge.y - pointY;
            const isTopEdge = topInset < bottomInset;
            const surfaceProfile = smoothEdge(-(isTopEdge ? topInset : bottomInset), edgeBand);
            const normalX = isTopEdge ? 0 : archEdge.normalX;
            const normalY = isTopEdge ? -1 : archEdge.normalY;
            const i = (y * w + x) * 4;
            data[i] = Math.round(Math.max(0, Math.min(1, 0.5 + normalX * surfaceProfile * 0.5)) * 255);
            data[i + 1] = Math.round(Math.max(0, Math.min(1, 0.5 + normalY * surfaceProfile * 0.5)) * 255);
            data[i + 2] = Math.round(Math.max(0, Math.min(1, 0.5 + normalY * surfaceProfile * 0.5)) * 255);
            data[i + 3] = 255;
        }
    }
    context.putImageData(image, 0, 0);
    return canvas.toDataURL();
}
const isChromium = () => typeof navigator !== "undefined" &&
    /Chrome|Chromium|Edg\//.test(navigator.userAgent);
/** Arch-shaped nav glass. Its lens map follows the same Bézier rim as its mask. */
export function NavRefract({ className = "", children, frost = 0, refract = true, mapBlur = LIQUID_MAP_BLUR, radius = 100, refraction = 100, depth = 36, dispersion = 80, ariaHidden = false, 
/**
 * Hide the rim against the bar's own container width rather than the
 * viewport's. Set by an inline MarketingNav, whose width is its frame's — see
 * the VIEWPORT vs FRAME note in marketing-nav.
 */
rimBreakpoint = "viewport", elementRef, onMouseEnter, onMouseLeave, style: surfaceStyle, ...props }) {
    const ref = useRef(null);
    const filterId = `nav-arch-glass-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
    const [state, setState] = useState(null);
    useLayoutEffect(() => {
        if (!isChromium() || !refract) {
            setState(null);
            return;
        }
        const element = ref.current;
        if (!element)
            return;
        let lastSize = { w: 0, h: 0 };
        const commit = () => {
            const w = Math.round(element.offsetWidth);
            const h = Math.round(element.offsetHeight);
            if (!w || !h)
                return;
            if (w === lastSize.w && h === lastSize.h)
                return;
            lastSize = { w, h };
            const map = makeArchDisplacementMap(w, h, Math.max(1, EDGE_BAND * (depth / 100)));
            if (map)
                setState({ map, w, h });
        };
        commit();
        // The bar's width is animated by CSS when it collapses. Unlike the hover
        // pill, the arch lens has to redraw for each observed size so the warped
        // backdrop remains attached to the moving notch rather than catching up
        // after the animation settles.
        const observer = new ResizeObserver(commit);
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [depth, refract]);
    const scale = MAX_SHIFT * 2 * (refraction / 80);
    const channelDispersion = DISPERSION * (dispersion / 50);
    const style = { ...surfaceStyle, borderRadius: radius };
    style.WebkitMaskImage = NAV_ARCH_MASK;
    style.WebkitMaskPosition = "left center, center, right center";
    style.WebkitMaskRepeat = "no-repeat";
    style.WebkitMaskSize = NAV_ARCH_MASK_SIZE;
    style.WebkitMaskClip = "border-box";
    style.maskImage = NAV_ARCH_MASK;
    style.maskMode = "alpha";
    style.maskPosition = "left center, center, right center";
    style.maskRepeat = "no-repeat";
    style.maskSize = NAV_ARCH_MASK_SIZE;
    if (!state) {
        style.backdropFilter = `blur(${frost}px) saturate(1.2)`;
        style.WebkitBackdropFilter = `blur(${frost}px) saturate(1.2)`;
    }
    else if (state) {
        style.backdropFilter = `blur(${frost}px) saturate(1.2) url(#${filterId})`;
        style.WebkitBackdropFilter = `blur(${frost}px) saturate(1.2) url(#${filterId})`;
    }
    return (_jsxs("header", { ref: (element) => {
            ref.current = element;
            elementRef?.(element);
        }, className: className, "data-refracting": state ? "" : undefined, style: style, "aria-hidden": ariaHidden, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, ...props, children: [state && (_jsx("svg", { width: "0", height: "0", className: "absolute", "aria-hidden": true, children: _jsxs("filter", { id: filterId, x: "0", y: "0", width: state.w, height: state.h, filterUnits: "userSpaceOnUse", colorInterpolationFilters: "sRGB", children: [_jsx("feImage", { href: state.map, x: "0", y: "0", width: state.w, height: state.h, result: "map" }), _jsx("feGaussianBlur", { in: "map", stdDeviation: mapBlur, result: "liquidMap" }), _jsx("feDisplacementMap", { in: "SourceGraphic", in2: "liquidMap", scale: scale * (1 + channelDispersion), xChannelSelector: "R", yChannelSelector: "B", result: "dispR" }), _jsx("feColorMatrix", { in: "dispR", type: "matrix", values: "1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0", result: "red" }), _jsx("feDisplacementMap", { in: "SourceGraphic", in2: "liquidMap", scale: scale, xChannelSelector: "R", yChannelSelector: "B", result: "dispG" }), _jsx("feColorMatrix", { in: "dispG", type: "matrix", values: "0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0", result: "green" }), _jsx("feDisplacementMap", { in: "SourceGraphic", in2: "liquidMap", scale: scale * (1 - channelDispersion), xChannelSelector: "R", yChannelSelector: "B", result: "dispB" }), _jsx("feColorMatrix", { in: "dispB", type: "matrix", values: "0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0", result: "blue" }), _jsx("feBlend", { in: "red", in2: "green", mode: "screen", result: "rg" }), _jsx("feBlend", { in: "rg", in2: "blue", mode: "screen" })] }) })), _jsx("div", { "data-nav-rim": true, className: `pointer-events-none absolute inset-0 z-[2] grid h-full w-full ${rimBreakpoint === "container"
                    ? "@max-[481px]/navbar:hidden"
                    : "max-[481px]:hidden"}`, style: {
                    gridTemplateColumns: `min(${NAV_ARCH_EDGE_WIDTH}px, 50%) minmax(0, 1fr) min(${NAV_ARCH_EDGE_WIDTH}px, 50%)`,
                }, "aria-hidden": true, children: NAV_ARCH_RIM_SLICES.map(([x, width]) => (_jsx("svg", { className: "h-full w-full min-w-0 overflow-hidden", viewBox: `${x} 0 ${width} ${INITIAL_NAV_ARCH_SIZE.h}`, preserveAspectRatio: "none", children: _jsx("path", { className: "fill-none stroke-[#8bc2db]/86 stroke-2 [vector-effect:non-scaling-stroke]", d: navArchRimPath(INITIAL_NAV_ARCH_SIZE.w, INITIAL_NAV_ARCH_SIZE.h) }) }, x))) }), children] }));
}
//# sourceMappingURL=nav-refract.js.map